/**
 * Analytics utility for tracking signed-out preview views and conversions
 */

import { BACKEND_URL } from './api';

const API = BACKEND_URL || window.location.origin;

// Generate or retrieve session ID for tracking
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('blk_analytics_session');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('blk_analytics_session', sessionId);
  }
  return sessionId;
};

// Store the source page for conversion tracking
export const setConversionSource = (page) => {
  sessionStorage.setItem('blk_conversion_source', page);
};

export const getConversionSource = () => {
  return sessionStorage.getItem('blk_conversion_source');
};

/**
 * Track when a signed-out user views a preview page
 * @param {string} page - The page being viewed ('locker', 'profile', 'marketplace', 'sets')
 */
export const trackPreviewView = async (page) => {
  try {
    await fetch(`${API}/api/analytics/track-view`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        page,
        event_type: 'preview_view',
        session_id: getSessionId(),
        referrer: document.referrer || null
      })
    });
    // Store source for potential conversion tracking
    setConversionSource(page);
  } catch (e) {
    // Silent fail - analytics shouldn't break the app
    console.debug('Analytics tracking error:', e);
  }
};

/**
 * Track when a user clicks a CTA button on a preview page
 * @param {string} page - The page with the CTA
 * @param {string} ctaType - The type of CTA ('signup_click', 'login_click', 'cta_click')
 */
export const trackCTAClick = async (page, ctaType = 'cta_click') => {
  try {
    await fetch(`${API}/api/analytics/track-view`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        page,
        event_type: ctaType,
        session_id: getSessionId(),
        referrer: document.referrer || null
      })
    });
    // Store source for conversion
    setConversionSource(page);
  } catch (e) {
    console.debug('Analytics CTA tracking error:', e);
  }
};

/**
 * Track when a signed-out view leads to a successful conversion
 * @param {string} eventType - The conversion type ('signup_complete', 'login_complete', 'first_purchase')
 */
export const trackConversion = async (eventType) => {
  try {
    const sourcePage = getConversionSource();
    if (!sourcePage) return; // No source page tracked
    
    await fetch(`${API}/api/analytics/track-conversion`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        source_page: sourcePage,
        event_type: eventType,
        session_id: getSessionId()
      })
    });
    
    // Clear the source after tracking conversion
    sessionStorage.removeItem('blk_conversion_source');
  } catch (e) {
    console.debug('Analytics conversion tracking error:', e);
  }
};

export default {
  trackPreviewView,
  trackCTAClick,
  trackConversion,
  setConversionSource,
  getConversionSource
};
